import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/chemistry/elements/theming/debug-display-component.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import '@adornis/popover/x-dropdown.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { PageFooter } from '@martinus/styles/client/PageFooter.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import type { Bundesland } from '../../../db/enums/misc-enums.js';
import { getAllInternalMartinusSchools } from '../../../groups/api/getAllSchools.js';
import { LastEditorInformation } from '../../../history/client/LastEditorInformation.js';
import { getSchoolIDsForRoles } from '../../../permissions/api/getSchoolIDsForRoles.js';
import { NoPermission } from '../../../permissions/client/NoPermission.js';
import { canMartinusAbac, useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { EMPLOYEE_ROLES } from '../../../permissions/roles.js';
import { Person } from '../../entities/person.js';
import type { UserOnUserPayload } from '../../UserPolicy.js';
import { ANAusbildung } from '../entities/employee-sections/an-ausbildung.js';
import { ANBeschaeftigung } from '../entities/employee-sections/an-beschaeftigung.js';
import { ANDokumente } from '../entities/employee-sections/an-dokumente.js';
import { ANEinverstaendnisErklaerungen } from '../entities/employee-sections/an-einverstaendniserklaerungen.js';
import { ANGeringfuegigeBeschaeftigung } from '../entities/employee-sections/an-geringfuegigebeschaeftigung.js';
import { ANInfos } from '../entities/employee-sections/an-infos.js';
import { ANPersonalakte } from '../entities/employee-sections/an-personalakte.js';
import { ANPrivatVersicherung } from '../entities/employee-sections/an-privatversicherung.js';
import { ANSchwerbehinderung } from '../entities/employee-sections/an-schwerbehinderung.js';
import { ANSozialVersicherung } from '../entities/employee-sections/an-sozialversicherung.js';
import { ANSteuerInfos } from '../entities/employee-sections/an-steuerinfos.js';
import { ANVermoegenswirksameLeistungen } from '../entities/employee-sections/an-vermoegenswirksameleistungen.js';
import { ANWorkingStudent } from '../entities/employee-sections/an-working-student.js';
import { MitarbeiterDaten } from '../entities/mitarbeiter-daten.js';
import { UserEmployeeDetailsActions } from '../UserEmployeeDetailsPolicy.js';
import {
  EmployeeSection,
  getMissingEmployeeSections,
  type SectionFormDefinition,
} from './section-forms/EmployeeSection.js';
import { EmployeeSectionAusbildungFormValidator } from './section-forms/EmployeeSectionAusbildungForm.js';
import { EmployeeSectionDokumenteFormValidator } from './section-forms/EmployeeSectionDokumenteForm.js';
import { EmployeeSectionDownloadsForm } from './section-forms/EmployeeSectionDownloadsForm.js';
import { EmployeeSectionEinverstaendniserklaerungenFormValidator } from './section-forms/EmployeeSectionEinverstaendniserklaerungenForm.js';
import { EmployeeSectionGeringfuegigeBeschaeftigungFormValidator } from './section-forms/EmployeeSectionGeringfuegigeBeschaeftigungForm.js';
import { EmployeeSectionInfosFormValidator } from './section-forms/EmployeeSectionInfosForm.js';
import { EmployeeSectionInternalIdentifiers } from './section-forms/EmployeeSectionInternalIdentifiers.js';
import { EmployeeSectionPrivatVersicherungFormValidator } from './section-forms/EmployeeSectionPrivatVersicherungForm.js';
import { EmployeeSectionSchwerbehinderungFormValidator } from './section-forms/EmployeeSectionSchwerbehinderungForm.js';
import { EmployeeSectionSozialVersicherungFormValidator } from './section-forms/EmployeeSectionSozialVersicherungForm.js';
import { EmployeeSectionSteuernFormValidator } from './section-forms/EmployeeSectionSteuernForm.js';
import { EmployeeSectionVermoegenswirksameLeistungenFormValidator } from './section-forms/EmployeeSectionVermoegenswirksameLeistungenForm.js';

const NoMitarbeiterDaten = stateful((props: { person: EntityData<Person> }) => {
  const { can: canEditSections } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS,
    viewedUser: props.person,
  });

  return html`
    <div class="flex h-full w-full items-center justify-center p-4">
      <div class="flex max-w-md flex-col items-center text-center">
        <div class="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-error/10">
          <span class="icon-[material-symbols--checked-bag-question] h-12 w-12 text-error"></span>
        </div>
        <h2 class="mb-2 font-semibold md:text-lg text-base-content">Keine Mitarbeiterdaten gefunden.</h2>
        ${
          canEditSections
            ? html`
                <button
                  class="btn btn-success btn-outline"
                  @click=${async () => {
                    props.person.mitarbeiterDaten ??= new MitarbeiterDaten({});
                    await props.person.save();
                  }}
                >
                  <span class="icon-[material-symbols--add] text-success"></span>
                  Neue Mitarbeiterdaten anlegen
                </button>
              `
            : nothing
        }
      </div>
    </div>
  `;
});

export const UserEmployeeDetailsSections = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');
  const user = useObservable(() => currentUser<Person>(), []);
  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        username: 1,
        email: 1,
        emailConfirmed: 1,
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        lastConfirmationEmailSent: 1,
        debitorennummer: 1,
        legacyDebitorNumbers: { debitorNumber: 1, scope: 1 },
        nameAbbreviation: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        profilDaten: { stammdaten: { geburtsDatum: 1, staatsangehoerigkeit: 1, staatsangehoerigkeit2: 1 } },
        mitarbeiterDaten: {
          lodasPersonalNr: 1,
          personalNr: 1,
          altePersonalNr: 1,
          anInfos: selectionSet(() => ANInfos, 2),
          anSteuerInfos: selectionSet(() => ANSteuerInfos, 2),
          anSozialVersicherung: selectionSet(() => ANSozialVersicherung, 1),
          anAusbildung: selectionSet(() => ANAusbildung, 1),
          anPrivatVersicherung: selectionSet(() => ANPrivatVersicherung, 1),
          anGeringfuegigeBeschaeftigung: selectionSet(() => ANGeringfuegigeBeschaeftigung, 2),
          anSchwerbehinderung: selectionSet(() => ANSchwerbehinderung, 2),
          anDokumente: selectionSet(() => ANDokumente, 1),
          anPersonalakte: selectionSet(() => ANPersonalakte, 1),
          anEinverstaendnisErklaerungen: selectionSet(() => ANEinverstaendnisErklaerungen, 1),
          anBeschaeftigung: selectionSet(() => ANBeschaeftigung, 1),
          anVermoegenswirksameLeitungen: selectionSet(() => ANVermoegenswirksameLeistungen, 2),
          anWorkingStudent: selectionSet(() => ANWorkingStudent, 1),
        },
      }),
    [props.personID],
  );

  const {
    can: canViewSections,
    loading: permissionLoading,
    reason,
  } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_DETAILS,
    viewedUser: person,
  });

  const { can: canEditSections } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS,
    viewedUser: person,
  });

  const { value: visibleSections, loading: visibleSectionsLoading } = useQuery(async () => {
    if (!user || !person) return [];

    const sections = await Promise.all(
      EmployeeSection.map(async section => ({
        section,
        allowed: await canMartinusAbac<UserOnUserPayload>({
          action: section.viewPermission,
          currentUser: user,
          viewedUser: person,
        }),
      })),
    );

    return sections.filter(value => value.allowed).map(value => value.section);
  }, [user?._id, person?._id]);

  const { value: bundeslandOfFirstPrimarySchool, loading: schoolLoading } = useQuery(
    () =>
      getSchoolIDsForRoles(person, EMPLOYEE_ROLES).then(async ids => {
        const firstSchool = ids[0];
        const schools = await getAllInternalMartinusSchools();
        const school = schools.find((s: { _id: string; bundesland: string }) => s._id === firstSchool);
        return school?.bundesland;
      }),
    [person?._id],
  );

  if (!user || personLoading || !person || schoolLoading || visibleSectionsLoading) return PageLoader({});

  if (!canViewSections && !permissionLoading) {
    return NoPermission({ message: reason });
  }

  if (!person.mitarbeiterDaten) return NoMitarbeiterDaten({ person: person as unknown as EntityData<Person> });

  /**
   * Cards state persisted so we can control them for errors
   */
  const [cardOpenStates, setCardOpenStates] = useState<Record<string, boolean>>({});

  /**
   * We need this as the deep change listening on the form clashes with the proxies set by adornisentity
   * TODO: remove this after the baseql rework
   */
  const [personObject] = useState(person.toObject(), [person._id]);

  const form = useForm(
    personObject,
    {
      enabledFields: canEditSections ? null : {},
      validator: {
        mitarbeiterDaten: {
          anInfos: EmployeeSectionInfosFormValidator,
          anSteuerInfos: EmployeeSectionSteuernFormValidator,
          anSozialVersicherung: EmployeeSectionSozialVersicherungFormValidator,
          anAusbildung: EmployeeSectionAusbildungFormValidator,
          anPrivatVersicherung: EmployeeSectionPrivatVersicherungFormValidator,
          anGeringfuegigeBeschaeftigung: EmployeeSectionGeringfuegigeBeschaeftigungFormValidator,
          anSchwerbehinderung: EmployeeSectionSchwerbehinderungFormValidator,
          anDokumente: EmployeeSectionDokumenteFormValidator(bundeslandOfFirstPrimarySchool as Bundesland),
          anEinverstaendnisErklaerungen: EmployeeSectionEinverstaendniserklaerungenFormValidator,
          anVermoegenswirksameLeitungen: EmployeeSectionVermoegenswirksameLeistungenFormValidator,
        },
      },
    },
    [canEditSections],
  );

  const handleSave = async () => {
    /**
     * Ensure and repair some fields
     * Remove children array if hatKinder is false
     */
    if (form.document.mitarbeiterDaten?.anInfos && !form.document.mitarbeiterDaten.anInfos.hatKinder) {
      form.document.mitarbeiterDaten.anInfos.kinder = [];
    }

    /**
     * Validate and open errounes sections if the user decides he wants to continue editing
     */
    const error = await form.validate();
    if (error && !(await openValidationErrorDialog({ error }))) {
      /**
       * Parse to array from multiple error
       */
      const validationErrors = error.extensions?.validationErrors as
        Array<{ extensions?: { path?: (string | number)[] } }> | undefined;

      const newOpenState: Record<string, boolean> = {};

      for (const error of validationErrors ?? []) {
        /**
         * The path is something like this: ['mitarbeiterDaten', 'anInfos', 'vorname']
         * We only want the second element (index 1) which is the section name
         */
        const section = error.extensions?.path?.[1];
        if (!section) continue;
        newOpenState[section] = true;
      }

      setCardOpenStates(newOpenState);
      return;
    }

    await guardDialog(
      async () => {
        await form.uploadAllPendingFiles();
        const value = constructValue(form.document) as Person;
        await value.save();
      },
      { successText: 'Person gespeichert' },
    );
  };

  const missingSection = getMissingEmployeeSections(form.document);
  const availableSections = visibleSections ?? [];

  const SectionRenderer = (section: SectionFormDefinition) => {
    const sectionData = form.document.mitarbeiterDaten?.[section.pathOnMitarbeiterDaten] as EntityData<unknown>;
    const isOpen = cardOpenStates[section.pathOnMitarbeiterDaten] ?? false;
    return sectionData
      ? Collapse({
          heading: section.title,
          open: isOpen,
          onToggle: (open: boolean) => {
            setCardOpenStates({ ...cardOpenStates, [section.pathOnMitarbeiterDaten]: open });
          },
          content: section.formComponent(form),
          headingActions: canEditSections
            ? html`
                <button
                  data-tip="${section.title} löschen"
                  ${hoverTooltip()}
                  class="btn btn-sm btn-ghost"
                  @click=${async (e: Event) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (!form.document.mitarbeiterDaten) return;
                    form.document.mitarbeiterDaten[section.pathOnMitarbeiterDaten] = null;
                  }}
                >
                  <span class="icon-[material-symbols--delete] text-lg"></span>
                </button>
              `
            : nothing,
        })
      : nothing;
  };

  return html`
    <div class="card-container">
      ${EmployeeSectionInternalIdentifiers({ form })}
      ${repeat(
        availableSections,
        section => section.title,
        section => SectionRenderer(section),
      )}
      ${Collapse({ heading: 'Downloads', content: EmployeeSectionDownloadsForm() })}
      ${
        canEditSections
          ? html`
              <div class="card card-body">
                ${DropdownSelectionInput({
                  selectables: missingSection,
                  renderItem: section => section.title,
                  placeholder: 'Neue Sektion anlegen',
                  renderString: () => '',
                  onPicked: (value: SectionFormDefinition | null) => {
                    if (!value || !form.document.mitarbeiterDaten) return;
                    // @ts-expect-error ts say its an abstract class. That is true in typing but the class itself will be instantiated.
                    form.document.mitarbeiterDaten[value.pathOnMitarbeiterDaten] = new value.ClassType({});
                  },
                })}
              </div>
            `
          : nothing
      }

      <div class="h-32"></div>
    </div>

    ${
      canEditSections
        ? html`
            ${PageFooter({
              content: html`
                ${LastEditorInformation({ referenceIds: [props.personID] })}
                <button class="btn btn-primary btn-lg" @click=${() => handleSave()}>
                  <span class="icon-[material-symbols--save]"></span>
                  Speichern
                </button>
              `,
            })}
          `
        : nothing
    }
  `;
});
